import { ReactiveController, ReactiveControllerHost } from "lit";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncFunction = (...args: any[]) => Promise<any>;

/**
 * Creates a declarative wrapper around async operations
 * @example
 * class MyElement extends LitElement {
 *   private user = new AsyncController(
 *     this,
 *     async ({ id }) => get(url, { id }));
 *
 *   async updated(changedProperties) {
 *     if (changedProperties.has("userId")) {
 *       await this.user.exec({ id: this.userId })
 *     }
 *   }
 *
 *   render() {
 *     const { data, loading, error } = this.user;
 *     if (loading) return html`Please wait...`
 *     if (error) return html`Error! <button @click=${this.user.retry}>Reload</button>`;
 *     return html`My name is: ${data.name}`
 *   }
 * }
 */
export class AsyncController<TFn extends AsyncFunction> implements ReactiveController {
  public data: Awaited<ReturnType<TFn>> | null = null;
  public loading = false;
  public error: Error | null = null;

  private readonly host: ReactiveControllerHost;
  private readonly asyncFn: TFn;
  private lastArgs?: Parameters<TFn>;

  constructor(host: ReactiveControllerHost, asyncFn: TFn) {
    (this.host = host).addController(this);
    this.asyncFn = asyncFn;
  }

  public async exec(...args: Parameters<TFn>): Promise<ReturnType<TFn> | null> {
    this.lastArgs = args;
    this.loading = true;
    this.host.requestUpdate();
    try {
      // TODO: prevent race condition if later invocation returns before earlier
      this.data = await this.asyncFn(...args);
      this.error = null;
    } catch (err) {
      if (err instanceof Error) {
        this.error = err;
        this.data = null;
      }
    }
    this.loading = false;
    this.host.requestUpdate();
    return this.data;
  }

  public async retry() {
    return this.exec(...((this.lastArgs || []) as Parameters<TFn>));
  }

  hostDisconnected() {
    // TODO: cancel API calls
  }
}
